export const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function onNextFrame(callback) {
  if (typeof globalThis.requestAnimationFrame === "function") globalThis.requestAnimationFrame(callback);
  else queueMicrotask(callback);
}

export function visibleFocusableElements(surface) {
  if (!surface?.querySelectorAll) return [];
  return [...surface.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
    if (element.hidden || element.getAttribute?.("aria-hidden") === "true") return false;
    return typeof element.getClientRects !== "function" || element.getClientRects().length > 0;
  });
}

export function trapSurfaceFocus(event, surface) {
  if (event?.key !== "Tab") return false;
  const focusable = visibleFocusableElements(surface);
  if (!focusable.length) {
    event.preventDefault?.();
    surface?.focus?.({ preventScroll: true });
    return true;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = surface?.ownerDocument?.activeElement;
  if (event.shiftKey && (active === first || !surface.contains?.(active))) {
    event.preventDefault?.();
    last.focus?.({ preventScroll: true });
    return true;
  }
  if (!event.shiftKey && (active === last || !surface.contains?.(active))) {
    event.preventDefault?.();
    first.focus?.({ preventScroll: true });
    return true;
  }
  return false;
}

export function createModalSurfaceController({
  surface,
  background = [],
  initialFocus,
  onRequestClose,
  modalRole = "dialog",
} = {}) {
  let active = false;
  let trigger = null;
  let addedRole = false;
  let addedTabIndex = false;
  const inertSnapshots = new Map();
  const documentRef = surface?.ownerDocument || globalThis.document;

  function keydown(event) {
    if (!active) return;
    if (event.key === "Escape") {
      event.preventDefault();
      onRequestClose?.();
      return;
    }
    trapSurfaceFocus(event, surface);
  }

  function activate(nextTrigger = documentRef?.activeElement) {
    if (!surface || active) return;
    active = true;
    trigger = nextTrigger instanceof Object ? nextTrigger : null;
    addedRole = !surface.hasAttribute?.("role");
    addedTabIndex = !surface.hasAttribute?.("tabindex");
    if (addedRole) surface.setAttribute?.("role", modalRole);
    surface.setAttribute?.("aria-modal", "true");
    if (addedTabIndex) surface.setAttribute?.("tabindex", "-1");
    for (const element of background.filter(Boolean)) {
      if (element === surface || element.contains?.(surface)) continue;
      inertSnapshots.set(element, Boolean(element.inert));
      element.inert = true;
    }
    documentRef?.addEventListener?.("keydown", keydown, true);
    const focusTarget = typeof initialFocus === "function" ? initialFocus() : initialFocus;
    onNextFrame(() => (focusTarget || visibleFocusableElements(surface)[0] || surface)?.focus?.({ preventScroll: true }));
  }

  function deactivate({ restoreFocus = true } = {}) {
    if (!active) return;
    active = false;
    documentRef?.removeEventListener?.("keydown", keydown, true);
    surface?.removeAttribute?.("aria-modal");
    if (addedRole) surface?.removeAttribute?.("role");
    if (addedTabIndex) surface?.removeAttribute?.("tabindex");
    addedRole = false;
    addedTabIndex = false;
    for (const [element, wasInert] of inertSnapshots) element.inert = wasInert;
    inertSnapshots.clear();
    const restoreTarget = trigger;
    if (restoreFocus && restoreTarget?.isConnected !== false) onNextFrame(() => restoreTarget?.focus?.({ preventScroll: true }));
    trigger = null;
  }

  return {
    activate,
    deactivate,
    isActive: () => active,
  };
}
